import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '@/lib/backendUrl';
import { applySeo, resetSeoToDefault } from '@/lib/seo';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Calendar, ChevronLeft, Tag } from 'lucide-react';

const SITE_ORIGIN = 'https://nfcesys.com.br';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

export default function BlogPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    applySeo({
      title: 'Blog nfcesysPdv | Automação comercial, PDV e gestão para pequenos negócios',
      description:
        'Artigos sobre automação comercial, sistemas de PDV, NFC-e, controle de estoque e gestão para pequenos e médios negócios brasileiros.',
      keywords: [
        'automação comercial',
        'sistema para pequeno negócio',
        'sistema PDV',
        'NFC-e',
        'sistemas no Brasil',
      ],
      canonical: `${SITE_ORIGIN}/blog`,
      type: 'website',
      image: `${SITE_ORIGIN}/og-image.png`,
      imageAlt: 'Blog nfcesysPdv',
      breadcrumbs: [
        { name: 'Home', url: `${SITE_ORIGIN}/` },
        { name: 'Blog', url: `${SITE_ORIGIN}/blog` },
      ],
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'Blog nfcesysPdv',
        url: `${SITE_ORIGIN}/blog`,
        description:
          'Artigos sobre automação comercial e sistemas de gestão para pequenos negócios.',
        publisher: {
          '@type': 'Organization',
          name: 'nfcesysPdv',
          logo: { '@type': 'ImageObject', url: `${SITE_ORIGIN}/logo.png` },
        },
      },
      jsonLdId: 'blog-list-jsonld',
    });
    return () => resetSeoToDefault();
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/blog?limit=50`);
        const data = await res.json();
        setPosts(data.posts || []);
      } catch (e) {
        console.error('Erro ao carregar artigos', e);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100"
      data-testid="blog-list-page"
    >
      {/* Header */}
      <header className="border-b border-slate-800/60 bg-slate-950/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            data-testid="blog-back-home"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="text-sm">Voltar para a home</span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="text-xl font-bold tracking-tight"
            data-testid="blog-logo"
          >
            nfcesys<span className="text-amber-400">Pdv</span>
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-10">
        <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/20 mb-4">
          Blog
        </Badge>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4">
          Conteúdo para quem quer{' '}
          <span className="text-amber-400">vender mais</span> e gerir melhor
        </h1>
        <p className="text-slate-400 text-base sm:text-lg max-w-2xl">
          Guias práticos sobre automação comercial, sistemas de PDV, NFC-e,
          controle de estoque e gestão para o pequeno e médio comércio brasileiro.
        </p>
      </section>

      {/* List */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        {loading ? (
          <div className="text-center py-20 text-slate-400" data-testid="blog-loading">
            Carregando artigos...
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-slate-400" data-testid="blog-empty">
            Nenhum artigo publicado ainda.
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {posts.map((post) => (
              <article
                key={post.id}
                className="group rounded-2xl border border-slate-800/80 bg-slate-900/50 hover:bg-slate-900 hover:border-amber-500/40 transition-all overflow-hidden flex flex-col"
                data-testid={`blog-card-${post.slug}`}
              >
                {post.cover_image && (
                  <Link to={`/blog/${post.slug}`} className="block">
                    <img
                      src={
                        post.cover_image.startsWith('http')
                          ? post.cover_image
                          : `${BACKEND_URL}${post.cover_image}`
                      }
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-44 object-cover transition-transform group-hover:scale-105"
                    />
                  </Link>
                )}
                <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                  <Calendar className="w-3.5 h-3.5" />
                  <time dateTime={post.published_at}>
                    {formatDate(post.published_at)}
                  </time>
                  {post.tags?.slice(0, 1).map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 ml-auto px-2 py-0.5 rounded-full bg-slate-800 text-slate-300"
                    >
                      <Tag className="w-3 h-3" />
                      {tag}
                    </span>
                  ))}
                </div>

                <h2 className="text-xl font-bold text-white mb-3 group-hover:text-amber-300 transition-colors leading-snug">
                  <Link
                    to={`/blog/${post.slug}`}
                    data-testid={`blog-card-link-${post.slug}`}
                  >
                    {post.title}
                  </Link>
                </h2>

                <p className="text-slate-400 text-sm leading-relaxed line-clamp-3 mb-4">
                  {post.excerpt}
                </p>

                <div className="mt-auto pt-4 border-t border-slate-800">
                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-medium"
                    data-testid={`blog-card-cta-${post.slug}`}
                  >
                    Ler artigo
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent p-8 md:p-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Pronto para automatizar seu negócio?
          </h2>
          <p className="text-slate-300 mb-6 max-w-xl mx-auto">
            Teste grátis o nfcesysPdv por 30 dias. Sem cartão de crédito, sem fidelidade.
          </p>
          <Button
            size="lg"
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
            onClick={() => navigate('/')}
            data-testid="blog-cta-trial"
          >
            Começar teste grátis
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}
